import { RoutineWorkflow } from '@gripp/shared-logic';
import {
  ColumnData,
  SlideAction,
  SlideLayout,
  SlideLayoutLocationState,
  useSlideLayoutSafe,
  ViewMode,
} from '@gripp/shared-ui';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { RoutinePreviewColumn } from '../../new-routines/components/routinePreviewColumn';
import { ProductAlertList } from './productAlertList';
import { ProductNewAlertHeaderAction } from './productNewAlertHeaderAction';

type RoutineAlertsLocationState = SlideLayoutLocationState &
  Record<string, unknown>;

const toLocationState = (state: unknown): RoutineAlertsLocationState | null =>
  state && typeof state === 'object' && !Array.isArray(state)
    ? (state as RoutineAlertsLocationState)
    : null;

// ─── Page ─────────────────────────────────────────────────────────────────────

export const ProductRoutineAlerts = () => {
  const { productId = '', routineId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const slideLayout = useSlideLayoutSafe();
  const locationState = toLocationState(location.state);
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;
  const backState = (() => {
    if (!locationState) return undefined;
    const { slideLayoutInitialTransition: _drop, ...rest } = locationState;
    return Object.keys(rest).length > 0 ? rest : undefined;
  })();

  if (!productId || !routineId) return null;

  const routineUrl = `/products/${productId}/routines/${routineId}`;

  const getStateWithTransition = (transition: SlideAction) => ({
    ...(backState ?? {}),
    slideLayoutInitialTransition: transition,
  });

  const handleAlertSelected = (selectedAlert: RoutineWorkflow) => {
    if (!selectedAlert?.id) return;
    const editPath = `/products/${productId}/alerts/${routineId}/${selectedAlert.id}`;
    slideLayout?.prepareSlideTransition(SlideAction.NEXT);
    navigate(editPath, {
      state: { slideLayoutInitialTransition: SlideAction.NEXT },
    });
  };

  const leftColumn: ColumnData = {
    title: 'product.detail.routines',
    mainComponent: (
      <RoutinePreviewColumn routineId={routineId} basePath={routineUrl} />
    ),
    showBackButton: true,
    disableScroll: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    backButtonTransitionExitColumn: 'both',
    onBackButtonPress: () =>
      navigate(routineUrl, {
        state: getStateWithTransition(SlideAction.PREV),
      }),
  };

  const rightColumn: ColumnData = {
    title: 'alerts.alerts',
    mainComponent: (
      <ProductAlertList
        productId={productId}
        routineId={routineId}
        onAlertSelected={handleAlertSelected}
      />
    ),
    headerComponent: (
      <ProductNewAlertHeaderAction
        productId={productId}
        routineId={routineId}
      />
    ),
    showBackButton: true,
    showHeader: true,
    disableScroll: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    onBackButtonPress: () =>
      navigate(routineUrl, {
        state: getStateWithTransition(SlideAction.PREV),
      }),
  };

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};
